import React, { useState } from 'react';
import { Tabs, Button, Typography } from 'antd';
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';
import { CodeLanguage, CodeSnippetParams } from '@core/@models/ApiManualModel';
import { generateCode } from '../utils/codeGenerators';
import { NotificationType, useNotification } from '@helpers/use-notification';

const { TabPane } = Tabs;
const { Text } = Typography;

interface CodeSnippetDisplayProps {
  apiType: string;
  params: CodeSnippetParams;
  disabled?: boolean;
}

const languageLabels: Record<CodeLanguage, string> = {
  curl: 'cURL',
  python: 'Python',
  java: 'Java',
  javascript: 'JavaScript',
};

export const CodeSnippetDisplay: React.FC<CodeSnippetDisplayProps> = ({
  apiType,
  params,
  disabled = false,
}) => {
  const [activeTab, setActiveTab] = useState<CodeLanguage>('curl');
  const [copied, setCopied] = useState(false);
  const notification = useNotification();

  const code = disabled
    ? '// Please fill in all required fields above'
    : generateCode(activeTab, apiType, params);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      notification.open({
        type: NotificationType.SUCCESS,
        description: 'Code copied to clipboard!',
        disableDate: true,
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      notification.open({
        type: NotificationType.ERROR,
        description: 'Failed to copy code',
        disableDate: true,
      });
    }
  };

  return (
    <div className="code-snippet-display">
      <div className="code-snippet-header">
        <Text strong>Generated Code</Text>
        <Button
          type="text"
          icon={
            copied ? (
              <CheckOutlined style={{ color: '#52c41a' }} />
            ) : (
              <CopyOutlined />
            )
          }
          onClick={handleCopy}
          disabled={disabled}
        >
          {copied ? 'Copied!' : 'Copy'}
        </Button>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as CodeLanguage)}
      >
        {(Object.keys(languageLabels) as CodeLanguage[]).map((lang) => (
          <TabPane tab={languageLabels[lang]} key={lang}>
            <pre className={`code-block ${disabled ? 'disabled' : ''}`}>
              <code>{generateCode(lang, apiType, params)}</code>
            </pre>
          </TabPane>
        ))}
      </Tabs>
    </div>
  );
};
